/* =========================================================
   Featured Video Grid / Carousel — behavior
   - Grid layout: simple click-to-zoom (unchanged)
   - Carousel layout: auto-swaps which item is "center" using a
     ping-pong sweep (0 -> last -> 0 -> ...). Items that move out of
     view get pushed to "hidden-left" / "hidden-right" so they slide
     toward the screen edge and disappear (clipped by the stage),
     rather than just fading in place.
   ========================================================= */

(function () {
  'use strict';

  function initOverlay(onOpenChange) {
    return function (overlay) {
      var closeBtn = overlay.querySelector('[data-fpv-close-btn]');
      var backdrop = overlay.querySelector('[data-fpv-backdrop]');
      var zoomInner = overlay.querySelector('[data-fpv-zoom-inner]');
      var zoomVideo = overlay.querySelector('.fpv-zoom-video');
      var isOpen = false;
      var lastTrigger = null;

      function onKeydown(e) {
        if (e.key === 'Escape' || e.key === 'Esc') close();
      }

      function open(triggerEl) {
        if (isOpen) return;
        isOpen = true;
        lastTrigger = triggerEl || null;

        overlay.classList.add('is-open');
        overlay.setAttribute('aria-hidden', 'false');
        document.body.classList.add('fpv-lock-scroll');

        if (triggerEl) {
          var previewVideo = triggerEl.querySelector('.fpv-preview-video');
          if (previewVideo && typeof previewVideo.pause === 'function') {
            previewVideo.pause();
          }
        }

        if (zoomVideo) {
          zoomVideo.currentTime = 0;
          var playPromise = zoomVideo.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(function () {});
          }
        }

        if (closeBtn) closeBtn.focus();
        document.addEventListener('keydown', onKeydown);
        if (onOpenChange) onOpenChange(true);

        // Diagnostic only (safe to remove): if some other script on the
        // page has a transform/filter/perspective on <html> itself, a
        // `position: fixed` element can stop covering the true viewport
        // and end up rendered in the normal page flow instead — which
        // looks exactly like "the video plays but there's no dark
        // background behind it". This just flags that case in the console.
        window.requestAnimationFrame(function () {
          var rect = overlay.getBoundingClientRect();
          var coversViewport =
            Math.abs(rect.top) < 1 &&
            Math.abs(rect.left) < 1 &&
            Math.abs(rect.width - window.innerWidth) < 1 &&
            Math.abs(rect.height - window.innerHeight) < 1;
          if (!coversViewport) {
            console.warn(
              '[featured-product-video] The video overlay/backdrop is not ' +
              'covering the full screen (rect: ' + JSON.stringify(rect) + ', ' +
              'viewport: ' + window.innerWidth + 'x' + window.innerHeight + '). ' +
              'This is usually caused by a transform, filter, perspective, or ' +
              'will-change style on <html> or <body> from another script/section ' +
              '(e.g. a smooth-scroll or page-transition library), which breaks ' +
              '"position: fixed" for this overlay.'
            );
          }
        });
      }

      function close() {
        if (!isOpen) return;
        isOpen = false;

        overlay.classList.remove('is-open');
        overlay.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('fpv-lock-scroll');

        if (zoomVideo && typeof zoomVideo.pause === 'function') {
          zoomVideo.pause();
        }

        if (lastTrigger) {
          var previewVideo = lastTrigger.querySelector('.fpv-preview-video');
          if (previewVideo && typeof previewVideo.play === 'function') {
            var p = previewVideo.play();
            if (p && typeof p.catch === 'function') p.catch(function () {});
          }
          lastTrigger.focus();
        }

        document.removeEventListener('keydown', onKeydown);
        if (onOpenChange) onOpenChange(false);
      }

      if (closeBtn) {
        closeBtn.addEventListener('click', function (e) {
          e.stopPropagation();
          close();
        });
      }
      if (backdrop) backdrop.addEventListener('click', close);
      if (zoomInner) {
        zoomInner.addEventListener('click', function (e) {
          e.stopPropagation();
        });
      }

      return { open: open, close: close };
    };
  }

  function initCarousel(section) {
    var stage = section.querySelector('[data-fpv-grid]');
    var items = Array.prototype.slice.call(section.querySelectorAll('.fpv-grid-item'));
    if (!stage || items.length === 0) return;

    var intervalMs = parseInt(section.getAttribute('data-carousel-interval'), 10) || 3000;
    var centerIndex = 0;
    var direction = 1;
    var timer = null;
    var openOverlaysCount = 0;

    function applyRoles() {
      items.forEach(function (item, idx) {
        var role;
        if (idx === centerIndex) {
          role = 'center';
        } else if (idx === centerIndex - 1) {
          role = 'left';
        } else if (idx === centerIndex + 1) {
          role = 'right';
        } else if (idx < centerIndex) {
          role = 'hidden-left';
        } else {
          role = 'hidden-right';
        }

        item.setAttribute('data-role', role);

        var trigger = item.querySelector('.fpv-grid-trigger');
        if (trigger) {
          var isVisible = role === 'center' || role === 'left' || role === 'right';
          if (!isVisible) {
            trigger.setAttribute('tabindex', '-1');
            trigger.setAttribute('aria-hidden', 'true');
          } else {
            trigger.removeAttribute('tabindex');
            trigger.removeAttribute('aria-hidden');
          }
        }
      });
    }

    function step() {
      if (items.length <= 1 || openOverlaysCount > 0) return;

      if (centerIndex >= items.length - 1) {
        direction = -1;
      } else if (centerIndex <= 0) {
        direction = 1;
      }
      centerIndex += direction;
      applyRoles();
    }

    function start() {
      if (timer || items.length <= 1) return;
      timer = window.setInterval(step, intervalMs);
    }

    function stop() {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    }

    applyRoles();
    start();

    // pause auto-swap while hovering the stage, or while a zoom overlay is open
    stage.addEventListener('mouseenter', stop);
    stage.addEventListener('mouseleave', function () {
      if (openOverlaysCount === 0) start();
    });

    section.__fpvCarouselOverlayChange = function (isOpen) {
      openOverlaysCount += isOpen ? 1 : -1;
      if (openOverlaysCount < 0) openOverlaysCount = 0;
      if (openOverlaysCount > 0) {
        stop();
      } else {
        start();
      }
    };

    // clicking a side item (left/right) brings it to center immediately
    items.forEach(function (item) {
      item.addEventListener('click', function (e) {
        if (stage.__fpvSuppressClick) {
          e.stopPropagation();
          e.preventDefault();
          return;
        }
        var role = item.getAttribute('data-role');
        if (role === 'left' || role === 'right') {
          var idx = parseInt(item.getAttribute('data-index'), 10);
          if (!isNaN(idx)) {
            centerIndex = idx;
            applyRoles();
          }
          // Side-item clicks should only recenter, not also open/play the
          // video underneath — stop it here so the bubble-phase open-zoom
          // handler on the button never fires for this click.
          e.stopPropagation();
          e.preventDefault();
        }
      }, true); // capture phase so this runs before the open-zoom click handler
    });

    // ---- Drag / swipe support: manually move through the whole list,
    // even while auto-swap is running. Works with mouse and touch via
    // Pointer Events. A short drag is ignored (treated as a click); a
    // drag past the threshold advances/retreats the center by one step
    // and suppresses the click-to-open so it doesn't fire by accident.
    var DRAG_THRESHOLD = 40; // px before it counts as an intentional swipe
    var CLICK_CANCEL_THRESHOLD = 10; // px of movement before a click is cancelled
    var dragState = null;

    function clampIndex(idx) {
      if (idx < 0) return 0;
      if (idx > items.length - 1) return items.length - 1;
      return idx;
    }

    function onPointerDown(e) {
      if (items.length <= 1) return;
      dragState = {
        startX: e.clientX,
        lastX: e.clientX,
        moved: false,
        pointerId: e.pointerId
      };
      stop();
      // NOTE: pointer capture is intentionally NOT set here. Capturing on
      // `stage` for every press (even a plain click) can prevent the click
      // event from ever reaching the trigger button in some browsers, which
      // is why the play button can appear completely dead. We only capture
      // once the movement actually crosses the drag threshold, in
      // onPointerMove below.
    }

    function onPointerMove(e) {
      if (!dragState) return;
      dragState.lastX = e.clientX;
      var delta = dragState.lastX - dragState.startX;
      if (Math.abs(delta) > CLICK_CANCEL_THRESHOLD) {
        if (!dragState.moved && stage.setPointerCapture) {
          try { stage.setPointerCapture(dragState.pointerId); } catch (err) {}
        }
        dragState.moved = true;
        stage.__fpvSuppressClick = true;
      }
    }

    function onPointerUp(e) {
      if (!dragState) return;
      var delta = dragState.lastX - dragState.startX;

      if (Math.abs(delta) >= DRAG_THRESHOLD) {
        if (delta < 0) {
          centerIndex = clampIndex(centerIndex + 1); // swiped left -> next
        } else {
          centerIndex = clampIndex(centerIndex - 1); // swiped right -> previous
        }
        // keep the ping-pong direction consistent with where the user left off
        direction = delta < 0 ? 1 : -1;
        applyRoles();
      }

      dragState = null;
      // small delay before re-enabling click, so the click event that
      // immediately follows pointerup on touch devices is still suppressed
      window.setTimeout(function () {
        stage.__fpvSuppressClick = false;
      }, 50);

      if (openOverlaysCount === 0) start();
    }

    stage.style.touchAction = 'pan-y'; // allow vertical page scroll, capture horizontal swipe
    stage.style.cursor = 'grab';
    stage.addEventListener('pointerdown', onPointerDown);
    stage.addEventListener('pointermove', onPointerMove);
    stage.addEventListener('pointerup', onPointerUp);
    stage.addEventListener('pointercancel', function () {
      dragState = null;
      stage.__fpvSuppressClick = false;
      if (openOverlaysCount === 0) start();
    });
  }

  function initSection(section) {
    var layoutStyle = section.getAttribute('data-layout-style');
    var overlayChangeHandler = function (isOpen) {
      if (section.__fpvCarouselOverlayChange) {
        section.__fpvCarouselOverlayChange(isOpen);
      }
    };

    var makeOverlay = initOverlay(overlayChangeHandler);
    var overlays = section.querySelectorAll('[data-fpv-zoom]');
    var overlayMap = {};

    overlays.forEach(function (overlayEl) {
      // Move the overlay to be a direct child of <html> (not just <body>).
      // Some themes wrap all page content in a scroll/animation container
      // with `transform` on <body> itself (common with smooth-scroll
      // libraries), which would still break `position: fixed` even after
      // moving to <body>. Attaching to <html> escapes that entirely.
      if (overlayEl.parentElement !== document.documentElement) {
        document.documentElement.appendChild(overlayEl);
      }

      // Belt-and-suspenders: force the critical positioning styles inline
      // with !important priority, so no external theme CSS (even another
      // !important rule with lower specificity) can accidentally override
      // them and prevent the fullscreen backdrop from showing.
      overlayEl.style.setProperty('position', 'fixed', 'important');
      overlayEl.style.setProperty('top', '0', 'important');
      overlayEl.style.setProperty('left', '0', 'important');
      overlayEl.style.setProperty('right', '0', 'important');
      overlayEl.style.setProperty('bottom', '0', 'important');
      overlayEl.style.setProperty('z-index', '2147483647', 'important');
      overlayEl.style.setProperty('margin', '0', 'important');
      overlayEl.style.setProperty('transform', 'none', 'important');

      var id = overlayEl.getAttribute('data-fpv-zoom');
      overlayMap[id] = makeOverlay(overlayEl);
    });

    var triggers = section.querySelectorAll('[data-fpv-open-btn]');
    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var targetId = trigger.getAttribute('data-fpv-target');
        var controller = overlayMap[targetId];
        if (controller) controller.open(trigger);
      });
    });

    if (layoutStyle === 'carousel') {
      initCarousel(section);
    }
  }

  function initAll() {
    var sections = document.querySelectorAll('.featured-product-video-grid');
    sections.forEach(function (section) {
      initSection(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.querySelector
      ? event.target.querySelector('.featured-product-video-grid')
      : null;
    if (section) initSection(section);
  });
})();
